import React, { useEffect, useState, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Wallet, Plus, Pencil, Trash2, ArrowDownCircle, ArrowUpCircle, Printer, FileCheck, Paperclip } from 'lucide-react';
import { useAuth } from '../AuthContext';
import LedgerEntryForm from '../components/LedgerEntryForm';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import ViewBillModal from '../components/ViewBillModal';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { openPrintWindow, buildPrintHTML } from '../utils/printUtils';

const fmt = (n) => `Rs.${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateIN = (s) => (s ? new Date(s).toLocaleDateString('en-IN') : '-');

const BranchLedger = () => {
  const { user, isAdmin, isAdminOnly } = useAuth();
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(user?.branch_id || '');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [deleteDlg, setDeleteDlg] = useState({ open: false, item: null });
  const [viewBill, setViewBill] = useState({ open: false, id: null, filename: null });

  const extraAllowed = Array.isArray(user?.allowed_branch_ids) ? user.allowed_branch_ids : [];
  const canPickAny = isAdmin() || ['Checker', 'Account', 'HR'].includes(user?.role);

  const fetchBranches = useCallback(async () => {
    try {
      const r = await api.get('/branches');
      const all = r.data || [];
      const visible = canPickAny
        ? all
        : all.filter((b) => b.id === user?.branch_id || extraAllowed.includes(b.id));
      setBranches(visible);
      if (!selectedBranchId && visible.length > 0) setSelectedBranchId(visible[0].id);
    } catch {
      toast.error('Failed to load branches');
    }
    // eslint-disable-next-line
  }, [user?.branch_id]);

  const fetchLedger = useCallback(async (silent = false) => {
    if (!selectedBranchId) return;
    if (!silent) setLoading(true);
    try {
      const r = await api.get(`/branch-ledger/${selectedBranchId}/entries`);
      setLedgerData(r.data);
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Failed to load branch ledger');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => { fetchBranches(); }, [fetchBranches]);
  useEffect(() => { fetchLedger(); }, [fetchLedger]);
  useAutoRefresh(() => fetchLedger(true));

  const handleSubmit = async (payload) => {
    try {
      if (editingEntry) {
        await api.put(`/branch-ledger/entries/${editingEntry.id}`, payload);
        toast.success('Entry updated');
      } else {
        await api.post(`/branch-ledger/${selectedBranchId}/entries`, payload);
        toast.success('Entry posted');
      }
      setEntryDialogOpen(false);
      setEditingEntry(null);
      fetchLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
      throw err;
    }
  };

  const openEdit = (entry) => {
    if (!isAdminOnly()) { toast.error('Only Admin can edit entries'); return; }
    setEditingEntry({
      ...entry,
      amount: entry.debit_amount || entry.credit_amount,
      date: entry.date ? new Date(entry.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    });
    setEntryDialogOpen(true);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/branch-ledger/entries/${deleteDlg.item.id}`);
      toast.success('Entry deleted');
      setDeleteDlg({ open: false, item: null });
      fetchLedger();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const acc = ledgerData?.account;
  const entries = ledgerData?.entries || [];
  const openingBal = ledgerData?.opening_balance || 0;
  const openingType = ledgerData?.opening_balance_type || 'credit';
  const totalDebit = ledgerData?.total_debit || 0;
  const totalCredit = ledgerData?.total_credit || 0;
  const closingBal = ledgerData?.closing_balance || 0;
  const isAllBranches = selectedBranchId === 'all' || !!ledgerData?.is_all_branches;
  // Column count for empty / loading rows
  // Base: Date, Head, Description, Ref, Bill, Debit, Credit, Balance = 8
  // + Branch (when all branches) = 1; + Actions (admin, single branch only) = 1
  const colspan = 8 + (isAllBranches ? 1 : 0) + (isAdminOnly() && !isAllBranches ? 1 : 0);

  const doPrint = () => {
    if (!acc) return;
    const rows = entries.map((e) => `
      <tr>
        <td>${dateIN(e.date)}</td>
        <td>${e.head_name || ''}</td>
        <td>${e.description || ''}</td>
        <td>${e.reference_no || ''}</td>
        <td class="text-right">${e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
        <td class="text-right">${e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
        <td class="text-right">${fmt(e.running_balance)}</td>
      </tr>
    `).join('');
    const html = buildPrintHTML(
      `Branch Ledger — ${acc.member_name}`,
      `
        <div class="mb-2 text-sm">Account: ${acc.account_name} • Opening: ${fmt(openingBal)} (${openingType})</div>
        <table class="w-full border">
          <thead><tr>
            <th>Date</th><th>Head</th><th>Description</th><th>Ref</th>
            <th class="text-right">Debit</th><th class="text-right">Credit</th><th class="text-right">Balance</th>
          </tr></thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr>
              <td colspan="4" class="text-right"><b>Totals</b></td>
              <td class="text-right"><b>${fmt(totalDebit)}</b></td>
              <td class="text-right"><b>${fmt(totalCredit)}</b></td>
              <td class="text-right"><b>${fmt(closingBal)}</b></td>
            </tr>
          </tfoot>
        </table>
      `
    );
    openPrintWindow(html);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="branch-ledger-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Wallet className="w-7 h-7 text-emerald-700" /> Branch Ledger
          </h1>
          <p className="text-sm text-slate-500 mt-1">Income &amp; Expenditure account per branch. Post entries the same way as Expenditures.</p>
        </div>
        <div className="flex items-center gap-2">
          {canPickAny && (
            <Select value={selectedBranchId} onValueChange={setSelectedBranchId}>
              <SelectTrigger className="w-64" data-testid="branch-picker"><SelectValue placeholder="Select branch" /></SelectTrigger>
              <SelectContent>
                {isAdmin() && (
                  <SelectItem value="all" data-testid="branch-picker-all">📊 All Branches (Consolidated)</SelectItem>
                )}
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" onClick={doPrint} disabled={!ledgerData} data-testid="print-branch-ledger">
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
          <Button
            onClick={() => { setEditingEntry(null); setEntryDialogOpen(true); }}
            disabled={!selectedBranchId || isAllBranches}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="post-branch-entry-btn"
            title={isAllBranches ? 'Select a specific branch to post an entry' : ''}
          >
            <Plus className="w-4 h-4 mr-1" /> Post Entry
          </Button>
        </div>
      </div>

      {/* Account summary */}
      {acc && (
        <div className="bg-white border border-emerald-200 rounded-xl px-5 py-4 mb-4 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-lg font-semibold text-slate-900">{acc.member_name}</div>
              <div className="text-sm text-slate-600">Account: <b>{acc.account_name}</b></div>
            </div>
            <div className="flex gap-3 flex-wrap">
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 min-w-[140px]">
                <div className="text-xs text-red-700 font-semibold flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" /> Total Debit</div>
                <div className="text-lg font-bold text-red-700">{fmt(totalDebit)}</div>
              </div>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 min-w-[140px]">
                <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" /> Total Credit</div>
                <div className="text-lg font-bold text-emerald-700">{fmt(totalCredit)}</div>
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 min-w-[140px]">
                <div className="text-xs text-slate-600 font-semibold">Entries</div>
                <div className="text-lg font-bold text-slate-900">{entries.length}</div>
              </div>
              <div className={`border rounded-lg px-4 py-2 min-w-[160px] ${closingBal >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <div className={`text-xs font-semibold ${closingBal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  Closing Balance ({closingBal >= 0 ? 'CR' : 'DR'})
                </div>
                <div className={`text-lg font-bold ${closingBal >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(Math.abs(closingBal))}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entries table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-emerald-700 text-white">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              {isAllBranches && <th className="px-3 py-2 text-left">Branch</th>}
              <th className="px-3 py-2 text-left">Particulars (Head)</th>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-left">Ref No.</th>
              <th className="px-3 py-2 text-center">Bill</th>
              <th className="px-3 py-2 text-right">Debit (Rs.)</th>
              <th className="px-3 py-2 text-right">Credit (Rs.)</th>
              <th className="px-3 py-2 text-right">Balance</th>
              {isAdminOnly() && !isAllBranches && <th className="px-3 py-2 text-center">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {!isAllBranches && (
              <tr className="bg-emerald-50/50">
                <td className="px-3 py-2 font-medium text-slate-700" colSpan={5}>Opening Balance ({openingType})</td>
                <td className="px-3 py-2 text-right text-red-700">{openingType === 'debit' ? fmt(openingBal) : fmt(0)}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{openingType === 'credit' ? fmt(openingBal) : fmt(0)}</td>
                <td className="px-3 py-2 text-right">-</td>
                {isAdminOnly() && <td></td>}
              </tr>
            )}
            {loading ? (
              <tr><td colSpan={colspan} className="px-3 py-6 text-center text-slate-400"><span className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-emerald-600"></span></td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={colspan} className="px-3 py-6 text-center text-slate-400 italic">No entries yet. Click &quot;Post Entry&quot; to add.</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50" data-testid="branch-ledger-row">
                <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{dateIN(e.date)}</td>
                {isAllBranches && <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{e.branch_name || '—'}</td>}
                <td className="px-3 py-2 font-medium text-slate-900">{e.head_name}</td>
                <td className="px-3 py-2 text-slate-600">{e.description || '-'}</td>
                <td className="px-3 py-2 text-xs font-mono text-slate-500">{e.reference_no || '-'}</td>
                <td className="px-3 py-2 text-center">
                  {e.has_bill ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-emerald-700 hover:bg-emerald-50"
                      onClick={() => setViewBill({ open: true, id: e.id, filename: e.bill_filename })}
                      data-testid={`view-bill-${e.id}`}
                      title="View bill"
                    >
                      <FileCheck className="w-4 h-4" />
                    </Button>
                  ) : (
                    <Paperclip className="w-3.5 h-3.5 text-slate-300 mx-auto" />
                  )}
                </td>
                <td className="px-3 py-2 text-right text-red-700 font-medium">{e.debit_amount ? fmt(e.debit_amount) : '-'}</td>
                <td className="px-3 py-2 text-right text-emerald-700 font-medium">{e.credit_amount ? fmt(e.credit_amount) : '-'}</td>
                <td className={`px-3 py-2 text-right font-semibold ${e.running_balance >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(e.running_balance)}</td>
                {isAdminOnly() && !isAllBranches && (
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-center">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)} className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50" data-testid={`edit-entry-${e.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteDlg({ open: true, item: e })} className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" data-testid={`delete-entry-${e.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Post / Edit Entry Dialog */}
      <Dialog open={entryDialogOpen} onOpenChange={(o) => { setEntryDialogOpen(o); if (!o) setEditingEntry(null); }}>
        <DialogContent className="max-w-lg" data-testid="post-branch-entry-dialog">
          <DialogHeader><DialogTitle>{editingEntry ? 'Edit Branch Ledger Entry' : 'Post Branch Ledger Entry'}</DialogTitle></DialogHeader>
          <LedgerEntryForm
            editing={!!editingEntry}
            allowBill
            initial={editingEntry ? {
              entry_type: editingEntry.entry_type,
              date: editingEntry.date,
              amount: editingEntry.amount,
              project_activity_id: editingEntry.project_activity_id || '',
              branch_id: editingEntry.branch_id || selectedBranchId,
              expenditure_head_id: editingEntry.expenditure_head_id || '',
              reference_no: editingEntry.reference_no || '',
              description: editingEntry.description || '',
            } : { branch_id: selectedBranchId }}
            lockedBranchId={selectedBranchId === 'all' ? undefined : selectedBranchId}
            onSubmit={handleSubmit}
            onCancel={() => { setEntryDialogOpen(false); setEditingEntry(null); }}
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteDlg.open}
        onOpenChange={(o) => setDeleteDlg({ open: o, item: o ? deleteDlg.item : null })}
        onConfirm={doDelete}
        title="Delete Ledger Entry"
        description="This action cannot be undone."
        itemName={deleteDlg.item ? `${deleteDlg.item.head_name} (${fmt(deleteDlg.item.debit_amount || deleteDlg.item.credit_amount)})` : ''}
      />

      {/* Inline bill viewer */}
      <ViewBillModal
        open={viewBill.open}
        onOpenChange={(o) => setViewBill({ open: o, id: o ? viewBill.id : null, filename: o ? viewBill.filename : null })}
        apiPath={viewBill.id ? `/branch-ledger/entries/${viewBill.id}/bill` : null}
        filename={viewBill.filename}
      />
    </div>
  );
};

export default BranchLedger;
